import type { AccountType, Page } from '../types';

export const KNOWN_PAGES: readonly Page[] = [
  'overview',
  'map',
  'plans',
  'biotope-plan',
  'customer-hub',
  'seeding',
  'activities',
  'tasks',
  'documents',
  'forest',
  'profile',
  'advisor',
  'catalog',
  'raccoon-map',
  'clients',
  'integrations',
  'superadmin',
  'admin-seeds',
  'admin-map',
  'admin-activity',
  'admin-email',
  'admin-traffic',
  'admin-stats',
] as const;

export function isKnownPage(page: string): page is Page {
  return (KNOWN_PAGES as readonly string[]).includes(page);
}

export interface ParsedRoute {
  estateId?: string;
  page?: Page;
  fieldId?: string;
}

export interface LandingPageOptions {
  accountType?: AccountType;
  isAdmin?: boolean;
  isRecovery?: boolean;
  isDemo?: boolean;
  estateCount?: number;
  routePage?: Page;
}

/**
 * Parses a hash or URL string into a ParsedRoute.
 *
 * Supported formats:
 * - #/revir/:estateId/:page/:fieldId
 * - #/revir/:estateId/:page
 * - #/revir/:estateId (defaults page to 'map')
 * - #/:page (e.g. #/catalog, #/superadmin, #/profile, #/clients)
 * - Fallback to query params: ?estate=...&tab=...&field=...
 * - Empty / clean URL returns {}
 */
export function parseRoute(hashOrUrl: string): ParsedRoute {
  if (!hashOrUrl || typeof hashOrUrl !== 'string') {
    return {};
  }

  const raw = hashOrUrl.trim();
  if (!raw || raw === '#' || raw === '/' || raw === '#/') {
    return {};
  }

  const result: ParsedRoute = {};

  let hashPart = '';
  let searchPart = '';

  const hashIndex = raw.indexOf('#');
  if (hashIndex !== -1) {
    hashPart = raw.slice(hashIndex + 1);
    const preHash = raw.slice(0, hashIndex);
    const preSearchIdx = preHash.indexOf('?');
    if (preSearchIdx !== -1) {
      searchPart = preHash.slice(preSearchIdx + 1);
    }
  } else {
    const searchIdx = raw.indexOf('?');
    if (searchIdx !== -1) {
      searchPart = raw.slice(searchIdx + 1);
    } else if (raw.includes('=')) {
      searchPart = raw;
    }
  }

  if (hashPart) {
    const hashQuestionIdx = hashPart.indexOf('?');
    if (hashQuestionIdx !== -1) {
      if (!searchPart) {
        searchPart = hashPart.slice(hashQuestionIdx + 1);
      }
      hashPart = hashPart.slice(0, hashQuestionIdx);
    }

    const cleanPath = hashPart.replace(/^\/+/, '').replace(/\/+$/, '');
    if (cleanPath) {
      const segments = cleanPath
        .split('/')
        .map((s) => {
          try {
            return decodeURIComponent(s);
          } catch {
            return s;
          }
        })
        .filter(Boolean);

      if (segments[0] === 'revir' || segments[0] === 'revirer') {
        if (segments[1]) {
          result.estateId = segments[1];
          if (segments[2]) {
            if (isKnownPage(segments[2])) {
              result.page = segments[2];
              if (segments[3]) {
                result.fieldId = segments[3];
              }
            } else {
              result.page = 'map';
              result.fieldId = segments[2];
            }
          } else {
            result.page = 'map';
          }
        }
      } else if (isKnownPage(segments[0])) {
        result.page = segments[0];
        if (segments[1]) {
          result.fieldId = segments[1];
        }
      }
    }
  }

  if (searchPart) {
    try {
      const params = new URLSearchParams(searchPart);
      const estateParam = params.get('estate') || params.get('estateId') || params.get('revir');
      if (estateParam && !result.estateId) {
        result.estateId = estateParam;
      }

      const pageParam = params.get('tab') || params.get('page');
      if (pageParam && isKnownPage(pageParam) && !result.page) {
        result.page = pageParam;
      }

      const fieldParam = params.get('field') || params.get('fieldId');
      if (fieldParam && !result.fieldId) {
        result.fieldId = fieldParam;
      }

      if (result.estateId && !result.page) {
        result.page = 'map';
      }
    } catch {
      // Ignore URLSearchParams parsing failures
    }
  }

  return result;
}

/**
 * Builds a canonical hash route string from a ParsedRoute.
 *
 * Rules:
 * - If route.estateId:
 *   - With fieldId: #/revir/${estateId}/${page || 'map'}/${fieldId}
 *   - Without fieldId: #/revir/${estateId}/${page || 'map'}
 * - Without estateId:
 *   - With page and fieldId: #/${page}/${fieldId}
 *   - With page: #/${page}
 *   - Otherwise: ''
 */
export function buildRoute(route: ParsedRoute): string {
  if (route.estateId) {
    const page = route.page || 'map';
    if (route.fieldId) {
      return `#/revir/${encodeURIComponent(route.estateId)}/${page}/${encodeURIComponent(route.fieldId)}`;
    }
    return `#/revir/${encodeURIComponent(route.estateId)}/${page}`;
  }

  if (route.page) {
    if (route.fieldId) {
      return `#/${route.page}/${encodeURIComponent(route.fieldId)}`;
    }
    return `#/${route.page}`;
  }

  return '';
}

/**
 * Synchronizes the browser URL with the specified route without reloading or creating duplicate history entries.
 */
export function syncUrlToRoute(route: ParsedRoute): void {
  if (typeof window === 'undefined') return;
  const targetHash = buildRoute(route);
  const currentHash = window.location.hash || '';

  if (currentHash === targetHash) return;

  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';
  const nextUrl = targetHash ? `${pathname}${search}${targetHash}` : `${pathname}${search}`;

  if (window.history && typeof window.history.replaceState === 'function') {
    window.history.replaceState(window.history.state, '', nextUrl);
  } else {
    window.location.hash = targetHash;
  }
}

/**
 * Determines the initial landing page for a user session.
 *
 * Rules:
 * - Special case: recovery link always lands on 'profile'
 * - Route page: if provided and valid, routePage is respected
 * - Special case: demo mode always lands on 'overview'
 * - Administrator always lands on 'superadmin'
 * - Professional account lands on 'clients' (Mine kunder)
 * - Standard account always lands on 'map' (Mine marker)
 */
export function determineLandingPage(options: LandingPageOptions = {}): Page {
  if (options.isRecovery) {
    return 'profile';
  }
  if (options.routePage && isKnownPage(options.routePage)) {
    return options.routePage;
  }
  if (options.isDemo) {
    return 'overview';
  }
  if (options.isAdmin) {
    return 'superadmin';
  }
  if (options.accountType === 'professional') {
    return 'clients';
  }
  return 'map';
}
